import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { errorMessage } from '../packaging/packingApi';
import { btn, dialog, input, overlay } from '../packaging/packingStyles';
import { searchSurplusIndexes, SurplusIndex } from './surplusApi';

interface Props {
  title: string;
  onClose: () => void;
  /** Saves the surplus; a rejected promise keeps the dialog open with the error. */
  onSave: (index: string, quantity: number) => Promise<unknown>;
}

const label: React.CSSProperties = { display: 'block', marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#334155' };

/**
 * Surplus added by hand (admin): pick an index — name, material, colour and dye come from its newest
 * production item — and type the quantity. The surplus has no line or week of its own.
 */
const ManualSurplusModal: React.FC<Props> = ({ title, onClose, onSave }) => {
  const { t } = useLanguage();
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SurplusIndex[]>([]);
  const [picked, setPicked] = useState<SurplusIndex | null>(null);
  const [quantity, setQuantity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Debounced index search; only the newest answer fills the list.
  useEffect(() => {
    if (picked) return;
    let current = true;
    const id = window.setTimeout(async () => {
      try {
        const result = await searchSurplusIndexes(search.trim());
        if (current) setOptions(result);
      } catch (e: any) {
        if (current) setError(errorMessage(e, t('manualSurplus.errorLoad')));
      }
    }, 250);
    return () => {
      current = false;
      window.clearTimeout(id);
    };
  }, [search, picked, t]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!picked) {
      setError(t('manualSurplus.pickIndex'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(picked.index, Number(quantity));
    } catch (err: any) {
      setError(errorMessage(err, t('manualSurplus.errorSave')));
      setSaving(false);
    }
  };

  const facts: [string, string | null][] = picked
    ? [
        [t('manualSurplus.name'), picked.name],
        [t('manualSurplus.material'), picked.material],
        [t('manualSurplus.color'), picked.color],
        [t('manualSurplus.dye'), picked.dye],
      ]
    : [];

  return (
    <div style={overlay} onClick={onClose}>
      <form style={dialog(520)} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2 style={{ margin: '0 0 6px', fontSize: 19, color: '#0f172a' }}>{title}</h2>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b' }}>{t('manualSurplus.hint')}</p>

        {picked ? (
          <div style={{ border: '1px solid #e2e8f0', borderLeft: '4px solid #667eea', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 16, color: '#0f172a' }}>{picked.index}</span>
              <button type="button" style={btn('ghost', true)} onClick={() => setPicked(null)}>{t('manualSurplus.change')}</button>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: '8px 0 0', fontSize: 13 }}>
              {facts.map(([name, value]) => (
                <React.Fragment key={name}>
                  <dt style={{ color: '#64748b' }}>{name}</dt>
                  <dd style={{ margin: 0, color: '#1e293b', whiteSpace: 'pre-line' }}>{value || '-'}</dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        ) : (
          <label style={label}>
            {t('manualSurplus.index')}
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('manualSurplus.searchPlaceholder')}
              autoFocus
              style={{ ...input, marginTop: 6, fontFamily: 'monospace' }}
            />
            <div role="listbox" style={{ marginTop: 6, maxHeight: 220, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
              {options.length === 0 ? (
                <div style={{ padding: 12, fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>{t('manualSurplus.noIndexes')}</div>
              ) : (
                options.map((o) => (
                  <button
                    key={o.index}
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => {
                      setPicked(o);
                      setError(null);
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none',
                      borderBottom: '1px solid #f1f5f9', background: '#fff', cursor: 'pointer', fontSize: 14,
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>{o.index}</span>
                    {o.name && <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: '#64748b' }}>{o.name}</span>}
                  </button>
                ))
              )}
            </div>
          </label>
        )}

        <label style={label}>
          {t('manualSurplus.quantity')}
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={100000}
            step={1}
            required
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            style={{ ...input, marginTop: 6 }}
          />
        </label>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontSize: 14, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" style={btn('ghost')} onClick={onClose}>{t('packing.modal.cancel')}</button>
          <button type="submit" style={{ ...btn('primary'), opacity: saving ? 0.6 : 1 }} disabled={saving}>{t('packing.modal.save')}</button>
        </div>
      </form>
    </div>
  );
};

export default ManualSurplusModal;
